import { useCallback } from 'react'
import { InventoryModelsMap, VisitorSchema } from '~/features/visitors/schemas'
import { useSimplePrompt } from '~/ui/hooks/useSimplePrompt'

type ValidateTotalProps = {
  formData: VisitorSchema
  modelsMapData: InventoryModelsMap
  onSubmit: (data: VisitorSchema) => void
}

export const useValidateTotal = () => {
  const { SimplePrompt, setOpen } = useSimplePrompt()

  const validateTotal = useCallback(
    ({ onSubmit, formData, modelsMapData }: ValidateTotalProps) => {
      const normalized = formData.product_ids?.reduce<{
        total: number
        products: Array<{ stock_code_v2: string; price: number }>
      }>(
        (acc, p) => {
          const model = modelsMapData[p]
          const total = model ? model.models_v2.price + acc.total : acc.total
          const products = model
            ? acc.products.concat({
                stock_code_v2: model.stock_code_v2,
                price: model.models_v2.price,
              })
            : acc.products
          return { ...acc, total, products }
        },
        { total: 0, products: [] }
      ) ?? { total: 0, products: [] }

      const isTotalValid = normalized.total === formData.total

      if (!isTotalValid && normalized.products.length > 0) {
        setOpen({
          title: 'Inconsistent Total!',
          text: 'The total price of the selected products is not equal to the total value in the form. Are you sure you want to proceed?',
          description: normalized.products
            .map((p) => `${p.stock_code_v2} - ${p.price} CZK`)
            .concat([
              '--------',
              `Total - ${formData.total} CZK`,
              `Difference - ${formData.total - normalized.products.reduce((acc, p) => (acc += p.price), 0)} CZK`,
            ])

            .join('\n'),
          onSubmit: () => onSubmit(formData),
        })
        return false
      }

      return true
    },
    [setOpen]
  )

  return { ValidateTotalDialog: SimplePrompt, validateTotal }
}
